import type { ColumnDef } from '@tanstack/react-table';
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { useDeviceModals } from '@/feature/device/hooks/useDeviceModals';
import type { IDeviceListItem } from '@/feature/device/types/device';
import { organizationService } from '@/feature/organization/services/organizationService';
import { userService } from '@/feature/user/services/userService';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import { useServerListTableState } from '@/shared/hooks/useServerListTableState';
import { formatDate } from '@/shared/utils/dateUtils';

import { ROUTES } from '../../routes/routeConstants';
import { getDeviceColumns } from '../Devices/deviceColumns';
import StatusBadge from '../common/StatusBadge';

import AddUserModal from './AddUserModal';
import ResendActivationModal from './ResendActivationModal';

interface IUser {
  id: number;
  apiId: string;
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Inactive';
  lastLogin: string;
}

interface IOrganizationDetails {
  id: number;
  organizationName: string;
  companyName?: string;
  industry: string;
  email: string;
  adminName: string;
  users: number;
  status: 'Active' | 'Inactive';
  created: string;
  description?: string;
}

const OrganizationDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isResendActivationModalOpen, setIsResendActivationModalOpen] = useState(false);
  const [isSendingActivationEmail, setIsSendingActivationEmail] = useState(false);
  const [organization, setOrganization] = useState<IOrganizationDetails | null>(null);
  const [orgDevices, setOrgDevices] = useState<IDeviceListItem[]>([]);
  const [deviceCount, setDeviceCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const hasSentActivationEmail = useRef(false);

  const { listParams, page, setPage, pageSize, search, setSearch, onSortChange, resetPage } =
    useServerListTableState({ pageSize: 10 });

  const [tableUsers, setTableUsers] = useState<IUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);

  const parseOrganizationId = (rawId: unknown): number => {
    if (typeof rawId === 'number') {
      return rawId;
    }
    if (typeof rawId === 'string') {
      const parsed = parseInt(rawId, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const fetchOrganization = useCallback(
    async (showLoader = true) => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      if (showLoader) {
        setIsLoading(true);
      }
      try {
        const apiOrg = await organizationService.getOrganizationById(id);

        if (apiOrg) {
          let apiUserCount = 0;
          if (Array.isArray(apiOrg.users)) {
            apiUserCount = apiOrg.users.length;
          } else if (typeof apiOrg.users === 'number') {
            apiUserCount = apiOrg.users;
          }
          const mappedOrg: IOrganizationDetails = {
            id: parseOrganizationId(apiOrg.id),
            organizationName: apiOrg.organizationName || apiOrg.org_name || apiOrg.name || '',
            companyName:
              (apiOrg as { companyName?: string; company_name?: string }).companyName ||
              (apiOrg as { companyName?: string; company_name?: string }).company_name ||
              '--',
            industry: apiOrg.industry || '',
            email: apiOrg.email || '',
            adminName: String(
              (apiOrg as Record<string, unknown>)['admin_name'] ||
                (apiOrg as Record<string, unknown>)['adminName'] ||
                ''
            ),
            users: apiUserCount,
            status: (apiOrg.status === 'Active' || apiOrg.is_active === true
              ? 'Active'
              : 'Inactive') as 'Active' | 'Inactive',
            created:
              (apiOrg as { created?: string }).created ||
              formatDate(apiOrg.created_at ? new Date(apiOrg.created_at) : new Date()),
            description: apiOrg.description || '',
          };
          setOrganization(mappedOrg);

          const devices = Array.isArray(apiOrg.devices)
            ? (apiOrg.devices as IDeviceListItem[])
            : [];
          setOrgDevices(devices);
          setDeviceCount(
            typeof apiOrg.device_count === 'number' ? apiOrg.device_count : devices.length
          );
        } else {
          setOrganization(null);
          setOrgDevices([]);
          setDeviceCount(0);
        }
      } catch {
        setOrganization(null);
        setOrgDevices([]);
        setDeviceCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [id]
  );

  // Fetch organization details
  useEffect(() => {
    void fetchOrganization(true);
    hasSentActivationEmail.current = false;
  }, [fetchOrganization]);

  const refreshOrgDevices = useCallback(async () => {
    await fetchOrganization(false);
  }, [fetchOrganization]);

  const { handleAction, modalElements } = useDeviceModals(refreshOrgDevices);
  const deviceColumns = useMemo(
    () => getDeviceColumns({ showRentedTo: false, onAction: handleAction }),
    [handleAction]
  );

  // Fetch accounts with server-side pagination
  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchAccounts = async () => {
      setIsAccountsLoading(true);
      try {
        const result = await userService.getAccountsByOrg(id, listParams);
        setTotalCount(result.pagination.total_count);
        setTableUsers(
          result.users.map((u, idx) => ({
            id: typeof u.id === 'number' ? u.id : parseInt(String(u.id ?? idx), 10) || idx,
            apiId: String(u.id ?? ''),
            name: String(u.name ?? ''),
            email: String(u.email ?? ''),
            role: String(u.role ?? ''),
            status: (u.status === 'Active' ? 'Active' : 'Inactive') as 'Active' | 'Inactive',
            lastLogin: u.lastLogin ? formatDate(new Date(String(u.lastLogin))) : '—',
          }))
        );
      } finally {
        setIsAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, [id, listParams]);

  const getInitials = useCallback((name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  }, []);

  const handleAddUser = useCallback(
    async (userData: { name: string; email: string; role: string }) => {
      const created = await userService.createUser({
        first_name: userData.name,
        email: userData.email,
        role: userData.role,
        org: id ?? '',
      });

      if (created) {
        toast.success('User added successfully');
        resetPage();
      }
    },
    [id, resetPage]
  );

  const getAdminNameFromStorage = useCallback((): string => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) {
        return '';
      }

      const user = JSON.parse(raw) as Record<string, unknown>;

      if (typeof user.admin_name === 'string' && user.admin_name.trim()) {
        return user.admin_name.trim();
      }

      const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
      if (fullName) {
        return fullName;
      }

      if (typeof user.name === 'string' && user.name.trim()) {
        return user.name.trim();
      }

      if (typeof user.username === 'string' && user.username.trim()) {
        return user.username.trim();
      }

      return '';
    } catch {
      return '';
    }
  }, []);

  const handleResendActivationEmail = useCallback(
    async (formData: { email: string; admin_name: string }) => {
      if (!id) {
        return;
      }
      setIsSendingActivationEmail(true);
      try {
        const ok = await organizationService.resendActivationMail(id, formData);
        if (ok) {
          toast.success('Activation email sent');
          setIsResendActivationModalOpen(false);
        }
      } finally {
        setIsSendingActivationEmail(false);
      }
    },
    [id]
  );

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => {
          const name = info.getValue() as string;
          return (
            <div className='flex items-center gap-3'>
              <div className='w-9 h-9 rounded-full bg-gray-200 text-gray-800 flex items-center justify-center text-xs font-normal shrink-0'>
                {getInitials(name)}
              </div>
              <span className='font-normal text-gray-900'>{name}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'role',
        header: 'Role',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue() as 'Active' | 'Inactive'} />,
      },
      {
        accessorKey: 'lastLogin',
        header: 'Last Login',
      },
      {
        id: 'actions',
        header: 'Action',
        cell: (info) => (
          <button
            type='button'
            className='inline-flex items-center justify-center p-1.5 rounded-md text-theme-600 hover:text-theme-700 hover:bg-theme-50/90 bg-transparent border-0 cursor-pointer transition-colors'
            aria-label='View user'
            onClick={() => navigate(ROUTES.USER_DETAILS.replace(':id', info.row.original.apiId))}
          >
            <svg
              className='shrink-0 w-4 h-4'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden={true}
            >
              <path
                d='M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <circle cx='8' cy='8' r='2' stroke='currentColor' strokeWidth='1.5' />
            </svg>
          </button>
        ),
      },
    ],
    [getInitials, navigate]
  );

  if (isLoading) {
    return (
      <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
        <nav className='flex items-center gap-1.5 text-sm mb-4' aria-label='Breadcrumb'>
          <Link to={ROUTES.DASHBOARD} className='text-theme-500 hover:opacity-80 flex items-center'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M3 9.5L10 3L17 9.5V17H13V13H7V17H3V9.5Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinejoin='round'
              />
            </svg>
          </Link>
          <span className='text-gray-400'>›</span>
          <Link to={ROUTES.ORGANIZATIONS} className='text-theme-500 hover:opacity-80'>
            Organizations
          </Link>
          <span className='text-gray-400'>›</span>
          <span className='text-gray-700'>Loading...</span>
        </nav>
        <div className='flex justify-center py-12 text-gray-600'>
          <p>Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
        <nav className='flex items-center gap-1.5 text-sm mb-4' aria-label='Breadcrumb'>
          <Link to={ROUTES.DASHBOARD} className='text-theme-500 hover:opacity-80 flex items-center'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M3 9.5L10 3L17 9.5V17H13V13H7V17H3V9.5Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinejoin='round'
              />
            </svg>
          </Link>
          <span className='text-gray-400'>›</span>
          <Link to={ROUTES.ORGANIZATIONS} className='text-theme-500 hover:opacity-80'>
            Organizations
          </Link>
          <span className='text-gray-400'>›</span>
          <span className='text-gray-700'>Not Found</span>
        </nav>
        <div className='text-red-600'>Organization not found</div>
      </div>
    );
  }

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
      <nav className='flex items-center gap-1.5 text-sm mb-6' aria-label='Breadcrumb'>
        <Link to={ROUTES.DASHBOARD} className='text-theme-500 hover:opacity-80 flex items-center'>
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M3 9.5L10 3L17 9.5V17H13V13H7V17H3V9.5Z'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinejoin='round'
            />
          </svg>
        </Link>
        <span className='text-gray-400'>›</span>
        <Link to={ROUTES.ORGANIZATIONS} className='text-theme-500 hover:opacity-80'>
          Organizations
        </Link>
        <span className='text-gray-400'>›</span>
        <span className='text-gray-700'>{organization.organizationName}</span>
      </nav>

      <div className='mb-6'>
        <h1 className='text-3xl font-bold text-gray-900 m-0'>{organization.organizationName}</h1>
      </div>

      <div className='card mb-8'>
        <div className='flex flex-col gap-4 mb-6'>
          <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
              INDUSTRY:
            </span>
            <span className='text-gray-900'>{organization.industry}</span>
          </div>
          {organization.description && (
            <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
              <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
                DESCRIPTION:
              </span>
              <span className='text-gray-900'>{organization.description}</span>
            </div>
          )}
        </div>
        <div className='flex flex-col sm:flex-row gap-6 pt-4 border-t border-gray-100'>
          <div className='flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
              STATUS:
            </span>
            <StatusBadge status={organization.status} />
            {organization.status === 'Inactive' && (
              <button
                type='button'
                className='inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-theme-500 text-theme-600 rounded-md hover:bg-theme-50 disabled:opacity-50 bg-white cursor-pointer'
                onClick={() => setIsResendActivationModalOpen(true)}
                title='Send Activation Email'
                aria-label='Send Activation Email'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M2 2.66667H14V13.3333H2V2.66667Z'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M2 2.66667L8 8L14 2.66667'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                Resend Activation
              </button>
            )}
          </div>
          <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
              CREATED:
            </span>
            <span className='text-gray-900'>{organization.created}</span>
          </div>
        </div>
      </div>

      <div className='mt-8'>
        <div className='flex justify-between items-center mb-4 flex-wrap gap-4'>
          <h2 className='text-xl font-bold text-gray-900 m-0'>Users ({totalCount})</h2>
          <button
            type='button'
            disabled={organization.status === 'Inactive'}
            className='bg-theme-500 text-white border-none px-4 py-2 rounded-md text-sm font-medium hover:bg-theme-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
            onClick={() => setIsAddUserModalOpen(true)}
          >
            + Add User
          </button>
        </div>

        <TanStackTable<IUser>
          data={tableUsers}
          columns={columns}
          loading={isAccountsLoading}
          emptyMessage='No users found'
          searchPlaceholder='Search users...'
          getRowId={(row) => String(row.id)}
          serverSidePagination={true}
          totalItems={totalCount}
          currentPage={page}
          onPageChange={setPage}
          pageSize={pageSize}
          serverSideSearch={true}
          serverSearchValue={search}
          onServerSearchChange={setSearch}
          manualSorting={true}
          onServerSortChange={(sorting) => {
            if (sorting.length > 0) {
              onSortChange(sorting[0].id, sorting[0].desc ? 'desc' : 'asc');
            }
          }}
          resetStateOnDataChange={false}
        />
      </div>

      <div className='mt-8'>
        <div className='flex justify-between items-center mb-2 flex-wrap gap-4'>
          <h2 className='text-xl font-bold text-gray-900 m-0'>Devices ({deviceCount})</h2>
        </div>
        <p className='text-sm text-gray-500 mb-4'>
          Devices currently rented or reserved by this organization.
        </p>
        <TanStackTable<IDeviceListItem>
          data={orgDevices}
          columns={deviceColumns}
          emptyMessage='No devices currently assigned to this organization.'
          searchPlaceholder='Search devices...'
          getRowId={(row) => row.id || row.device_id}
        />
      </div>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onAdd={handleAddUser}
        organizationName={organization.organizationName}
      />

      <ResendActivationModal
        isOpen={isResendActivationModalOpen}
        onClose={() => setIsResendActivationModalOpen(false)}
        onSend={handleResendActivationEmail}
        defaultEmail={organization.email}
        defaultAdminName={organization?.adminName || getAdminNameFromStorage()}
        isSending={isSendingActivationEmail}
      />
      {modalElements}
    </div>
  );
};

export default OrganizationDetailsPage;
